import { addMinutes, differenceInSeconds, set } from 'date-fns';
import { observer } from 'mobx-react-lite';
import {
  CSSProperties,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import Icon from '~/components/Icon/Icon';
import { Time } from '~/constants/variables';
import { useDateLang } from '~/hooks/useDateLang';
import { useGlobal } from '~/hooks/useGlobal';
import { useLocalStorage } from '~/hooks/useLocalStorage';
import { useMounted } from '~/hooks/useMounted';
import { useTimeFormat } from '~/hooks/useTimeFormat';

import { orderStore } from '~/stores';

import styles from './DeliveryTimeWarning.module.scss';

type DeliveryInfo = ReturnType<typeof orderStore.getDeliveryLabel>;

const DURATION = 10000;
const DELAY_FOR_SLOT = 10;
const DELAY_FOR_EXPRESS = 30;
const MAX_TIMESTAMPS_DIFF = 30;

const getExpressDeliveryExpireTime = (
  expressDelivery: DeliveryInfo['expressDelivery'] | undefined,
) => {
  if (!expressDelivery) {
    return null;
  }

  return dateByTime(expressDelivery.closing).valueOf();
};

const getSlotExpireTime = (slot: DeliveryInfo['slot'] | undefined) => {
  if (!slot) {
    return null;
  }

  return slot.cut_off_time * 1000;
};

const dateByTime = (time: string) => {
  const [h, m, s] = time.split(':');
  const toNumber = (value: unknown) => Number(value) || 0;

  return set(new Date(), {
    milliseconds: 0,
    seconds: toNumber(s),
    minutes: toNumber(m),
    hours: toNumber(h),
  });
};

type Schedule = Record<
  string,
  {
    timestamp: number;
    minutesToDelay: number;
    isDone: boolean;
    openedAt: Nullable<number>;
  }
>;

interface Props {
  buttonRef?: React.RefObject<HTMLElement>;
}

export const DeliveryTimeWarning: React.FC<Props> = observer(
  ({ buttonRef }) => {
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const { isMobile } = useGlobal();
    const mounted = useMounted();
    const [message, setMessage] = useState('');
    const [style, setStyle] = useState<CSSProperties>({});
    const [position, setPosition] = useState({ top: 0, right: 0, bottom: 0 });
    const [schedule, setSchedule] = useLocalStorage<Schedule>(
      'cartNotification',
      {},
    );
    const { timeFormat } = useTimeFormat();
    const { locale } = useDateLang();

    const { expressDelivery, slot } = orderStore.getDeliveryLabel({
      timeFormat,
      locale,
    });

    const calculatePosition = useCallback(() => {
      const button = buttonRef?.current;

      if (!button) {
        if (isMobile) {
          setPosition({
            top: 0,
            bottom: 94,
            right: 19,
          });
        }
        return;
      }

      const rect = button.getBoundingClientRect();
      const rightOffset = window.innerWidth - rect.right;
      const gap = isMobile ? 14 : 18;

      if (isMobile) {
        setPosition({
          top: 0,
          bottom: window.innerHeight - rect.top + gap,
          right: rightOffset + 3,
        });
      } else {
        setPosition({
          top: rect.bottom + gap,
          bottom: 0,
          right: rightOffset,
        });
      }
    }, [buttonRef, isMobile]);

    useLayoutEffect(() => {
      calculatePosition();
    }, [calculatePosition]);

    const closeNotification = useCallback((message: string) => {
      setStyle({});
      setSchedule((schedule) => {
        const item = schedule?.[message];

        return {
          ...schedule,
          [message]: {
            isDone: true,
            openedAt: item?.openedAt || null,
            timestamp: item?.timestamp || Date.now(),
            minutesToDelay: item?.minutesToDelay || 0,
          },
        };
      });
    }, []);

    const scheduleNotification = useCallback(
      (expireTimestamp: number, minutesToDelay: number, message: string) => {
        setSchedule((schedule) => {
          const scheduleItem = schedule?.[message];

          if (
            !scheduleItem ||
            scheduleItem.minutesToDelay !== minutesToDelay ||
            Math.abs(
              differenceInSeconds(scheduleItem.timestamp, expireTimestamp),
            ) > MAX_TIMESTAMPS_DIFF
          ) {
            return {
              ...schedule,
              [message]: {
                minutesToDelay,
                timestamp: expireTimestamp,
                isDone: false,
                openedAt: null,
              },
            };
          }

          return schedule;
        });
      },
      [],
    );

    useEffect(() => {
      const slotExpire = getSlotExpireTime(slot);
      const deliveryExpire = getExpressDeliveryExpireTime(expressDelivery);
      const now = Date.now();

      if (deliveryExpire && deliveryExpire > now) {
        scheduleNotification(
          deliveryExpire,
          DELAY_FOR_EXPRESS,
          'expressDeliveryNotification',
        );
      }

      if (
        slotExpire &&
        deliveryExpire &&
        Math.abs(differenceInSeconds(slotExpire, deliveryExpire)) <
          MAX_TIMESTAMPS_DIFF
      ) {
        scheduleNotification(
          Math.max(now, Math.min(slotExpire, deliveryExpire)),
          DELAY_FOR_SLOT,
          'termsChangingNotification',
        );
      } else if (!deliveryExpire && slotExpire && slotExpire > now) {
        scheduleNotification(
          slotExpire,
          DELAY_FOR_SLOT,
          'slotExpireNotification',
        );
      }
    }, [
      JSON.stringify(expressDelivery),
      JSON.stringify(slot),
      scheduleNotification,
    ]);

    useEffect(() => {
      if (!schedule) {
        return;
      }

      const timeouts: NodeJS.Timeout[] = [];

      Object.entries(schedule).forEach(([msg, item]) => {
        const { timestamp, minutesToDelay, isDone, openedAt } = item;
        const time =
          addMinutes(timestamp, -Math.abs(minutesToDelay)).valueOf() -
          Date.now();
        const isAvailablePage =
          pathname === '/' ||
          pathname === '/shop' ||
          pathname.includes('/c/') ||
          pathname.includes('/p/');

        if (!isAvailablePage || time < -Time.Minute) {
          return;
        }

        if (!isDone) {
          const timeout = setTimeout(
            () => {
              calculatePosition();
              setMessage(msg);
              setStyle({ opacity: 1, pointerEvents: 'unset' });
              setSchedule((prev) => ({
                ...prev,
                [msg]: { ...item, isDone: true, openedAt: Date.now() },
              }));
            },
            Math.max(time, 0),
          );

          timeouts.push(timeout);
        } else if (time < 0 && openedAt && Date.now() - openedAt < DURATION) {
          calculatePosition();
          setMessage(msg);
          setStyle({
            opacity: 1,
            pointerEvents: 'unset',
            transition: 'unset',
          });
        }
      });

      return () => {
        timeouts.forEach(clearTimeout);
      };
    }, [schedule, pathname, calculatePosition]);

    if (!mounted) {
      return null;
    }

    const notification = (
      <div
        role="alert"
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'fixed',
          ...(isMobile ? { bottom: position.bottom } : { top: position.top }),
          right: position.right,
          ...style,
        }}
        className={styles.notification}
        onClick={(event) => event.stopPropagation()}
      >
        <Icon type="time" size={20} aria-hidden="true" />
        <span className={styles.content}>{t(message)}</span>
        <button
          className={styles.close}
          aria-label={t('closeNotification')}
          onClick={() => closeNotification(message)}
        >
          {t('okButton')}
        </button>
      </div>
    );

    return createPortal(notification, document.body);
  },
);

export default DeliveryTimeWarning;
